import type { ApiErrorBody, ErrorCode } from '@an-crm/shared';

export type ClientErrorCode = ErrorCode | 'NETWORK_ERROR' | 'UNKNOWN';

/** Typed error thrown by the API client for every non-2xx response or network failure. */
export class ApiError extends Error {
  readonly code: ClientErrorCode;
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(opts: {
    code: ClientErrorCode;
    message: string;
    status: number;
    details?: unknown;
    requestId?: string;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.code = opts.code;
    this.status = opts.status;
    this.details = opts.details;
    this.requestId = opts.requestId;
  }
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false;
  const err = (value as { error: unknown }).error;
  return typeof err === 'object' && err !== null && 'code' in err && 'message' in err;
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

const FRIENDLY: Partial<Record<ClientErrorCode, string>> = {
  INVALID_CREDENTIALS: 'That email and password combination didn’t match. Please try again.',
  ACCOUNT_LOCKED:
    'This account is temporarily locked after too many attempts. Try again later or contact your administrator.',
  MFA_INVALID: 'That code didn’t work. Check your authenticator app and try again.',
  TENANT_SUSPENDED: 'This organisation is currently suspended. Please contact your administrator.',
  RATE_LIMITED: 'Too many attempts. Please wait a moment and try again.',
  FORBIDDEN: 'You don’t have permission to do that.',
  MODULE_NOT_ENTITLED: 'This module isn’t enabled for your organisation.',
  NOT_FOUND: 'We couldn’t find what you were looking for.',
  CONFLICT: 'That conflicts with an existing record.',
  UNAUTHENTICATED: 'Your session has expired. Please sign in again.',
  NETWORK_ERROR: 'We couldn’t reach the server. Check your connection and try again.',
  INTERNAL: 'Something went wrong on our side. Please try again.',
};

/** Human-friendly message for any thrown value. Validation/conflict messages from the API are kept. */
export function friendlyMessage(error: unknown): string {
  if (isApiError(error)) {
    if ((error.code === 'VALIDATION_FAILED' || error.code === 'CONFLICT') && error.message)
      return error.message;
    return FRIENDLY[error.code] ?? error.message ?? 'Something went wrong.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong.';
}
